/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactSelect from "react-select";
import { Table, Modal, Spin, Select } from "antd";
import alertify from "alertifyjs";
import { customStyles } from "../../../utils";
import {
  useGetBranchesQuery,
  useGetDepartmentsQuery,
  useGetDesignationsQuery,
} from "../../../services/settings.service";
import { useLazySearchEmployeesQuery } from "../../../services/employee.service";
import {
  useSendBroadcastMutation,
  usePreviewRecipientsMutation,
  useGetBroadcastHistoryQuery,
} from "../../../services/notification.service";

const { Option } = Select;

const Communication = () => {
  const [messageType, setMessageType] = useState("sms");
  const [title, setTitle] = useState("");
  const [gender, setGender] = useState("all");
  const [recipientType, setRecipientType] = useState("all");
  const [departmentId, setDepartmentId] = useState(null);
  const [designationId, setDesignationId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [message, setMessage] = useState("");
  const [previewCount, setPreviewCount] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  // RTK Query hooks
  const { data: branchesData } = useGetBranchesQuery();
  const { data: departmentsData } = useGetDepartmentsQuery();
  const { data: designationsData } = useGetDesignationsQuery();
  const [searchEmployees, { data: searchData }] = useLazySearchEmployeesQuery();
  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } = useGetBroadcastHistoryQuery();

  const [sendBroadcast, { isLoading: isSending }] = useSendBroadcastMutation();
  const [previewRecipients, { isLoading: isPreviewing }] = usePreviewRecipientsMutation();

  // Update employee options when search results come in
  useEffect(() => {
    if (searchData?.data) {
      setEmployeeOptions(searchData.data);
    }
  }, [searchData]);

  // Search employees handler
  const handleEmployeeSearch = (value) => {
    if (value && value.length >= 2) {
      searchEmployees({ q: value });
    } else {
      setEmployeeOptions([]);
    }
  };

  // Transform data for select options
  const branches = branchesData?.data?.map((b) => ({ value: b.id, label: b.name })) || [];
  const departments = departmentsData?.data?.map((d) => ({ value: d.id, label: d.name })) || [];
  const designations = designationsData?.data?.map((d) => ({ value: d.id, label: d.name })) || [];

  const genderOptions = [
    { value: "all", label: "All Genders" },
    { value: "Male", label: "Male Only" },
    { value: "Female", label: "Female Only" },
  ];

  // Character count for SMS
  const charCount = message.length;
  const smsPartsCount = Math.ceil(charCount / 160) || 1;

  // Handle preview
  const handlePreview = async () => {
    try {
      const filters = {
        message_type: messageType,
        gender: gender,
        recipient_type: recipientType,
        department_id: departmentId || undefined,
        designation_id: designationId || undefined,
        branch_id: branchId || undefined,
        employee_ids: recipientType === "selected" ? selectedEmployeeIds : undefined,
      };

      const response = await previewRecipients(filters).unwrap();
      if (response.success) {
        setPreviewCount(response.data.count);
        alertify.success(`Found ${response.data.count} recipient(s)`);
      }
    } catch (error) {
      alertify.error(error?.data?.message || "Failed to preview recipients");
    }
  };

  // Handle send
  const handleSend = async () => {
    // Validation
    if (!message.trim()) {
      alertify.error("Please enter a message");
      return;
    }

    if (messageType === "email" && !title.trim()) {
      alertify.error("Please enter a title for the email");
      return;
    }

    if (recipientType === "selected" && selectedEmployeeIds.length === 0) {
      alertify.error("Please select at least one employee");
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmSend = async () => {
    setShowConfirmModal(false);

    try {
      const payload = {
        message_type: messageType,
        title: messageType === "email" ? title : undefined,
        gender: gender,
        recipient_type: recipientType,
        department_id: departmentId || undefined,
        designation_id: designationId || undefined,
        branch_id: branchId || undefined,
        employee_ids: recipientType === "selected" ? selectedEmployeeIds : undefined,
        message: message,
      };

      const response = await sendBroadcast(payload).unwrap();

      if (response.success) {
        alertify.success(response.message);
        // Reset form
        setMessage("");
        setTitle("");
        setPreviewCount(null);
        refetchHistory();
      } else {
        alertify.error(response.message);
      }
    } catch (error) {
      alertify.error(error?.data?.message || "Failed to send broadcast");
    }
  };

  // History table columns
  const historyColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Type",
      dataIndex: "message_type",
      key: "message_type",
      render: (type) => (
        <span className={`badge ${type === "sms" ? "bg-info" : "bg-primary"}`}>
          {type.toUpperCase()}
        </span>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) => title || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const map = {
          processing: "bg-warning",
          completed: "bg-success",
          failed: "bg-danger",
        };
        return (
          <span className={`badge ${map[status] || "bg-secondary"}`}>
            {(status || "completed").toUpperCase()}
          </span>
        );
      },
    },
    {
      title: "Recipients",
      dataIndex: "total_recipients",
      key: "total_recipients",
    },
    {
      title: "Success",
      dataIndex: "successful_count",
      key: "successful_count",
      render: (count) => <span className="text-success">{count}</span>,
    },
    {
      title: "Failed",
      dataIndex: "failed_count",
      key: "failed_count",
      render: (count) => <span className="text-danger">{count}</span>,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Communication</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin-dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Communication</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Broadcast Form */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">Send Broadcast Message</h4>
              </div>
              <div className="card-body">
                {/* Message Type */}
                <div className="row mb-3">
                  <label className="col-lg-3 col-form-label">Message Type</label>
                  <div className="col-lg-9">
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn ${messageType === "sms" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setMessageType("sms")}
                      >
                        <i className="la la-mobile me-1"></i> SMS
                      </button>
                      <button
                        type="button"
                        className={`btn ${messageType === "email" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setMessageType("email")}
                      >
                        <i className="la la-envelope me-1"></i> Email
                      </button>
                    </div>
                  </div>
                </div>

                {/* Title (Email only) */}
                {messageType === "email" && (
                  <div className="row mb-3">
                    <label className="col-lg-3 col-form-label">Title/Subject</label>
                    <div className="col-lg-9">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter email subject"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* Gender */}
                <div className="row mb-3">
                  <label className="col-lg-3 col-form-label">Gender</label>
                  <div className="col-lg-9">
                    <ReactSelect
                      options={genderOptions}
                      value={genderOptions.find((g) => g.value === gender)}
                      onChange={(option) => setGender(option.value)}
                      styles={customStyles}
                    />
                  </div>
                </div>

                {/* Recipient Type */}
                <div className="row mb-3">
                  <label className="col-lg-3 col-form-label">Recipients</label>
                  <div className="col-lg-9">
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn ${recipientType === "all" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setRecipientType("all")}
                      >
                        All Staff
                      </button>
                      <button
                        type="button"
                        className={`btn ${recipientType === "selected" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setRecipientType("selected")}
                      >
                        Selected Staff
                      </button>
                    </div>
                  </div>
                </div>

                {/* Filters (for All Staff) */}
                {recipientType === "all" && (
                  <>
                    <div className="row mb-3">
                      <label className="col-lg-3 col-form-label">Department</label>
                      <div className="col-lg-9">
                        <ReactSelect
                          options={departments}
                          value={departments.find((d) => d.value === departmentId)}
                          onChange={(option) => setDepartmentId(option?.value || null)}
                          isClearable
                          placeholder="All Departments"
                          styles={customStyles}
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <label className="col-lg-3 col-form-label">Designation</label>
                      <div className="col-lg-9">
                        <ReactSelect
                          options={designations}
                          value={designations.find((d) => d.value === designationId)}
                          onChange={(option) => setDesignationId(option?.value || null)}
                          isClearable
                          placeholder="All Designations"
                          styles={customStyles}
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <label className="col-lg-3 col-form-label">Branch</label>
                      <div className="col-lg-9">
                        <ReactSelect
                          options={branches}
                          value={branches.find((b) => b.value === branchId)}
                          onChange={(option) => setBranchId(option?.value || null)}
                          isClearable
                          placeholder="All Branches"
                          styles={customStyles}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Employee Select (for Selected Staff) */}
                {recipientType === "selected" && (
                  <div className="row mb-3">
                    <label className="col-lg-3 col-form-label">Select Employees</label>
                    <div className="col-lg-9">
                      <Select
                        mode="multiple"
                        showSearch
                        size="large"
                        style={{ width: '100%' }}
                        placeholder="Type to search employees..."
                        value={selectedEmployeeIds}
                        onChange={setSelectedEmployeeIds}
                        onSearch={handleEmployeeSearch}
                        filterOption={false}
                        notFoundContent={null}
                      >
                        {employeeOptions.map((emp) => (
                          <Option key={emp.id} value={emp.id}>
                            {emp.name}
                            {emp.email ? ` (${emp.email})` : ""}
                          </Option>
                        ))}
                      </Select>
                      {selectedEmployeeIds.length > 0 && (
                        <small className="text-muted">
                          {selectedEmployeeIds.length} employee(s) selected
                        </small>
                      )}
                    </div>
                  </div>
                )}

                {/* Message */}
                <div className="row mb-3">
                  <label className="col-lg-3 col-form-label">Message</label>
                  <div className="col-lg-9">
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Enter your message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    {messageType === "sms" && (
                      <div className="d-flex justify-content-between mt-1">
                        <small className={charCount > 160 ? "text-warning" : "text-muted"}>
                          {charCount} characters
                        </small>
                        <small className="text-muted">
                          {smsPartsCount} SMS part(s)
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview Count */}
                {previewCount !== null && (
                  <div className="row mb-3">
                    <div className="col-lg-9 offset-lg-3">
                      <div className="alert alert-info mb-0">
                        <i className="la la-users me-2"></i>
                        <strong>{previewCount}</strong> recipient(s) will receive this message
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="row">
                  <div className="col-lg-9 offset-lg-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary me-2"
                      onClick={handlePreview}
                      disabled={isPreviewing}
                    >
                      {isPreviewing ? (
                        <Spin size="small" />
                      ) : (
                        <>
                          <i className="la la-eye me-1"></i> Preview Recipients
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSend}
                      disabled={isSending || !message.trim()}
                    >
                      {isSending ? (
                        <Spin size="small" />
                      ) : (
                        <>
                          <i className="la la-paper-plane me-1"></i> Send Broadcast
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">Quick Info</h4>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <h6>SMS Guidelines</h6>
                  <ul className="small text-muted">
                    <li>Standard SMS: 160 characters</li>
                    <li>Longer messages are split into multiple parts</li>
                    <li>Each part is charged separately</li>
                  </ul>
                </div>
                <div>
                  <h6>Email Guidelines</h6>
                  <ul className="small text-muted">
                    <li>Always include a clear subject</li>
                    <li>Keep message concise and professional</li>
                    <li>No character limit</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Broadcast History */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">Broadcast History</h4>
              </div>
              <div className="card-body">
                <Table
                  dataSource={historyData?.data || []}
                  columns={historyColumns}
                  loading={historyLoading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal
          title="Confirm Broadcast"
          open={showConfirmModal}
          onOk={confirmSend}
          onCancel={() => setShowConfirmModal(false)}
          okText="Send Now"
          okButtonProps={{ danger: true }}
        >
          <p>Are you sure you want to send this broadcast?</p>
          <ul>
            <li><strong>Type:</strong> {messageType.toUpperCase()}</li>
            {messageType === "email" && <li><strong>Subject:</strong> {title}</li>}
            <li><strong>Recipients:</strong> {previewCount ?? "Click 'Preview Recipients' to see count"}</li>
          </ul>
          <p className="text-warning">
            <i className="la la-exclamation-triangle me-1"></i>
            This action cannot be undone.
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default Communication;
